<template>
  <div class="relatorio-ia-bot-page">
    <HeroImpactoBot />

    <FiltrosRelatorioBot
      :store-options="storeSelectOptions"
      :chat-selecionado="chatSelecionado"
      :periodo-preset-id="periodoPresetId"
      :periodo-inicio="periodoInicio"
      :periodo-fim="periodoFim"
      :carregando="carregando"
      :disabled="!chatSelecionado"
      @update:chatSelecionado="onLojaChange"
      @update:periodoInicio="onUpdatePeriodoInicio"
      @update:periodoFim="onUpdatePeriodoFim"
      @preset="onPresetPeriodo"
      @aplicar-periodo="aplicarPeriodoEBuscar"
    />

    <header class="mc-sec-head">
      <h2 class="mc-sec-head__title">
        {{ periodoSectionTitle }}
      </h2>
      <p class="mc-sec-head__sub">
        {{ periodoTriggerRange }}
      </p>
      <div
        class="mc-sec-head__escopo"
        role="note"
      >
        <span
          class="mc-sec-head__escopo-ico"
          aria-hidden="true"
        >
          <feather-icon
            icon="LayersIcon"
            size="20"
          />
        </span>
        <p class="mc-sec-head__escopo-texto">
          <strong>Período e escopo:</strong>
          totais de conversas, percentuais de participação e resolução, bloco comparativo Bot × Humano e os três cards inferiores consideram apenas
          <strong>atendimentos finalizados</strong>
          (status
          <strong>resolvido</strong>)
          no intervalo de datas acima. Estimativas de tempo e custo no topo e no gráfico usam os registros da API nesse mesmo intervalo.
        </p>
      </div>
    </header>

    <b-row class="mc-main-row">
      <b-col
        cols="12"
        lg="8"
        class="mc-col-left"
      >
        <CardsResumoBot :report="reportView" />

        <p
          v-if="reportView.storytellingFrase"
          class="mc-impacto-story"
        >
          {{ reportView.storytellingFrase }}
        </p>

        <GraficoEconomiaBot
          :titulo="reportView.graficoTitulo"
          :subtitulo="reportView.graficoSubtitulo"
          :insight-linha="reportView.graficoInsightReducao"
          :labels="reportView.economiaLabels"
          :horas-com-bot="reportView.graficoHorasComBot"
          :horas-sem-bot="reportView.graficoHorasSemBot"
          :anotacao-label="reportView.graficoAnotacaoLabel"
        />

        <ComparativoBotHumano
          :resumo="reportView.resumoExecutivo"
          :blocos="reportView.comparativoBlocos"
        />

        <CardsRodapeBot
          :total-conversas="reportView.totalConversas"
          :resolvidas-bot="reportView.conversasSemIntervencao"
          :transferencias="reportView.transferencias"
          :variacao-pct="reportView.resolvidasVariacaoPct"
        />
      </b-col>

      <b-col
        cols="12"
        lg="4"
        class="mc-col-right"
      >
        <InsightsPeriodoBot :items="reportView.insights" />

        <QualidadeBot
          :csat="reportView.csat"
          :pct-sem-transferencia="reportView.pctSemTransferencia"
        />

        <div class="mc-aux">
          <div class="mc-aux__ico">
            <feather-icon
              icon="ActivityIcon"
              size="18"
            />
          </div>
          <div>
            <div class="mc-aux__k">
              {{ reportView.metricaAuxValor }}
            </div>
            <div class="mc-aux__t">
              {{ reportView.metricaAuxTitulo }}
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <div
      v-if="useMockData"
      class="mc-mock-banner"
    >
      Modo visual com dados de exemplo. Defina <code>useMockData: false</code> em <code>relatorio-ia-bot.vue</code> para usar os dados do período (API).
    </div>
  </div>
</template>

<script>
import { buscaChatsTeste, buscaResolvidos, buscaMensagensPorBot } from '@/functions/basics/chat'
import HeroImpactoBot from './relatorio-ia-bot/HeroImpactoBot.vue'
import FiltrosRelatorioBot from './relatorio-ia-bot/FiltrosRelatorioBot.vue'
import CardsResumoBot from './relatorio-ia-bot/CardsResumoBot.vue'
import InsightsPeriodoBot from './relatorio-ia-bot/InsightsPeriodoBot.vue'
import GraficoEconomiaBot from './relatorio-ia-bot/GraficoEconomiaBot.vue'
import ComparativoBotHumano from './relatorio-ia-bot/ComparativoBotHumano.vue'
import QualidadeBot from './relatorio-ia-bot/QualidadeBot.vue'
import CardsRodapeBot from './relatorio-ia-bot/CardsRodapeBot.vue'
import {
  calcularTempoBotSegundos,
  createMockReport,
  mapApiStateToReport,
} from './relatorio-ia-bot/relatorio-ia-bot-report'

/**
 * b-form-datepicker emite string 'YYYY-MM-DD'. `new Date('2026-03-05')` é UTC meia-noite;
 * em fusos como America/Sao_Paulo vira 4/mar local — exibe e compara o dia errado.
 */
function parseDataSomenteDataLocal(value) {
  if (value == null || value === '') return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, day] = value.trim().split('-').map(Number)
    return new Date(y, m - 1, day)
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const x = new Date(value)
  if (Number.isNaN(x.getTime())) return null
  return new Date(x.getFullYear(), x.getMonth(), x.getDate())
}

/** YYYY-MM-DD pelo calendário local (evita toISOString() trocar o dia em alguns fusos). */
function localDateToYmd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dateToKey(d) {
  const x = parseDataSomenteDataLocal(d) || new Date(d)
  if (!x || Number.isNaN(x.getTime())) return ''
  const y = x.getFullYear()
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const MAX_PERIODO_DIAS = 366

export default {
  name: 'RelatorioIaBot',
  components: {
    HeroImpactoBot,
    FiltrosRelatorioBot,
    CardsResumoBot,
    InsightsPeriodoBot,
    GraficoEconomiaBot,
    ComparativoBotHumano,
    QualidadeBot,
    CardsRodapeBot,
  },
  data() {
    const fim = new Date()
    const inicio = new Date()
    inicio.setDate(inicio.getDate() - 6)
    return {
      /** Altere para false para exibir métricas calculadas a partir da API no período selecionado. */
      useMockData: false,
      facebookAccountsLista: [],
      chatSelecionado: null,
      carregando: false,
      periodoInicio: inicio,
      periodoFim: fim,
      periodoPresetId: 'ultimos7',
      resolvidos: [],
      numeroConversas: 0,
      conversasResolvidasPeloBot: 0,
      totalMensagensBot: 0,
      tempoResolucaoMediaMin: 0,
      totalRespostasCsat: 0,
      mediaCsat: 0,
      resolucaoPorDia: {},
      transferenciasPorDia: {},
    }
  },
  computed: {
    storeSelectOptions() {
      return (this.facebookAccountsLista || []).map(conta => ({
        value: conta.chat_token,
        text: conta.name,
      }))
    },
    periodoTriggerRange() {
      const i = parseDataSomenteDataLocal(this.periodoInicio)
      const f = parseDataSomenteDataLocal(this.periodoFim)
      if (!i || !f) return ''
      const fmt = d => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(i)} — ${fmt(f)}`
    },
    periodoSectionTitle() {
      const map = {
        hoje: 'Hoje',
        ontem: 'Ontem',
        ultimos7: 'Últimos 7 dias',
        ultimos15: 'Últimos 15 dias',
        ultimos30: 'Últimos 30 dias',
        ultimos31: 'Últimos 31 dias',
        esteMes: 'Este mês',
        mesPassado: 'Mês passado',
        personalizado: 'Período personalizado',
      }
      return map[this.periodoPresetId] || 'Período selecionado'
    },
    reportView() {
      const titulo = this.periodoSectionTitle
      if (this.useMockData) return createMockReport(titulo)
      return mapApiStateToReport(this.$data, titulo)
    },
  },
  watch: {
    periodoInicio() { this.sincronizarPresetComDatas() },
    periodoFim() { this.sincronizarPresetComDatas() },
  },
  mounted() {
    this.carregarLojas().then(() => {
      if (!this.useMockData) this.carregarDados()
    })
  },
  methods: {
    onUpdatePeriodoInicio(v) {
      this.periodoInicio = parseDataSomenteDataLocal(v)
    },
    onUpdatePeriodoFim(v) {
      this.periodoFim = parseDataSomenteDataLocal(v)
    },
    onLojaChange(token) {
      this.chatSelecionado = token
      if (!this.useMockData) this.carregarDados()
    },
    onPresetPeriodo(id) {
      this.periodoPresetId = id
      if (id === 'personalizado') {
        return
      }
      this.aplicarDatasDoPreset(id)
      if (!this.useMockData) this.carregarDados()
    },
    aplicarDatasDoPreset(id) {
      const fim = new Date()
      const inicio = new Date()
      if (id === 'hoje') {
        this.periodoInicio = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate())
        this.periodoFim = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate())
        return
      }
      if (id === 'ontem') {
        const o = new Date(fim)
        o.setDate(o.getDate() - 1)
        this.periodoInicio = new Date(o.getFullYear(), o.getMonth(), o.getDate())
        this.periodoFim = new Date(o.getFullYear(), o.getMonth(), o.getDate())
        return
      }
      if (id === 'ultimos7') {
        inicio.setDate(fim.getDate() - 6)
        this.periodoInicio = inicio
        this.periodoFim = fim
        return
      }
      if (id === 'ultimos15') {
        inicio.setDate(fim.getDate() - 14)
        this.periodoInicio = inicio
        this.periodoFim = fim
        return
      }
      if (id === 'ultimos31' || id === 'ultimos30') {
        inicio.setDate(fim.getDate() - 30)
        this.periodoInicio = inicio
        this.periodoFim = fim
        return
      }
      if (id === 'esteMes') {
        this.periodoInicio = new Date(fim.getFullYear(), fim.getMonth(), 1)
        this.periodoFim = fim
        return
      }
      if (id === 'mesPassado') {
        const first = new Date(fim.getFullYear(), fim.getMonth() - 1, 1)
        const last = new Date(fim.getFullYear(), fim.getMonth(), 0)
        this.periodoInicio = first
        this.periodoFim = last
      }
    },
    limparPeriodo() {
      this.periodoPresetId = 'ultimos7'
      this.aplicarDatasDoPreset('ultimos7')
      if (!this.useMockData) this.carregarDados()
    },
    sincronizarPresetComDatas() {
      const i = parseDataSomenteDataLocal(this.periodoInicio)
      const f = parseDataSomenteDataLocal(this.periodoFim)
      if (!i || !f || Number.isNaN(i.getTime()) || Number.isNaN(f.getTime())) return
      const startDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      const today = new Date()
      const today0 = startDay(today)
      const y = new Date(today)
      y.setDate(y.getDate() - 1)
      const y0 = startDay(y)
      if (startDay(i) === today0 && startDay(f) === today0) {
        this.periodoPresetId = 'hoje'
        return
      }
      if (startDay(i) === y0 && startDay(f) === y0) {
        this.periodoPresetId = 'ontem'
        return
      }
      const diff = Math.round((f - i) / (1000 * 60 * 60 * 24)) + 1
      const firstEsteMes = new Date(today.getFullYear(), today.getMonth(), 1)
      if (startDay(i) === startDay(firstEsteMes) && startDay(f) === today0) {
        this.periodoPresetId = 'esteMes'
        return
      }
      const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0)
      if (startDay(i) === startDay(firstPrev) && startDay(f) === startDay(lastPrev)) {
        this.periodoPresetId = 'mesPassado'
        return
      }
      if (diff === 7) this.periodoPresetId = 'ultimos7'
      else if (diff === 15) this.periodoPresetId = 'ultimos15'
      else if (diff === 31) this.periodoPresetId = 'ultimos31'
      else if (diff === 30) this.periodoPresetId = 'ultimos31'
      else this.periodoPresetId = 'personalizado'
    },
    aplicarPeriodoEBuscar() {
      this.sincronizarPresetComDatas()
      if (!this.useMockData) this.carregarDados()
    },
    async carregarLojas() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []
      if ((contas.chats || []).length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if ((contas.chats || []).length > 1) {
        this.chatSelecionado = (contas.chats[0] && contas.chats[0].chat_token) || null
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', { title: 'Erro', variant: 'danger', solid: true })
      }
    },
    async carregarDados() {
      if (this.useMockData) return
      if (!this.chatSelecionado) return
      const inicio = parseDataSomenteDataLocal(this.periodoInicio)
      const fim = parseDataSomenteDataLocal(this.periodoFim)
      if (!inicio || !fim) {
        this.$bvToast.toast('Selecione data inicial e final.', { title: 'Atenção', variant: 'warning', solid: true })
        return
      }
      if (inicio > fim) {
        this.$bvToast.toast('A data de início não pode ser maior que a data de fim.', { title: 'Atenção', variant: 'warning', solid: true })
        return
      }
      const diffDias = Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24)) + 1
      if (diffDias > MAX_PERIODO_DIAS) {
        this.$bvToast.toast(`O período não pode exceder ${MAX_PERIODO_DIAS} dias.`, { title: 'Atenção', variant: 'warning', solid: true })
        return
      }

      this.carregando = true
      const uuid = this.chatSelecionado
      const inicioISO = localDateToYmd(inicio)
      const fimISO = localDateToYmd(fim)

      try {
        const [dados, mensagensBotData] = await Promise.all([
          buscaResolvidos(uuid, inicioISO, fimISO, 'closed'),
          buscaMensagensPorBot(uuid, inicioISO, fimISO).catch(() => null),
        ])
        const lista = dados?.resolvidos || []
        this.resolvidos = lista

        const statusResolvido = r => (r.status === 'resolvido' || r.status === 'RESOLVIDO')
        const temFimBot = r => r.fim_bot != null && r.fim_bot !== ''

        /** Transferido: fim da fase do bot + atendente humano associado (não entra em “resolvido só pelo bot”). */
        const ehAtendimentoTransferido = r => temFimBot(r) && r.atendente_ativo != null

        /**
         * Os três cards do rodapé usam o mesmo universo: só atendimentos finalizados (resolvidos).
         * Antes, transferidos incluía não finalizados — por isso o total (39) podia ser menor que bot+transf.
         */
        const listaFinalizados = lista.filter(statusResolvido)
        this.numeroConversas = listaFinalizados.length

        const resolvidasPeloBot = listaFinalizados.filter(
          r => r.atendimento_bot === true && !ehAtendimentoTransferido(r),
        )
        this.conversasResolvidasPeloBot = resolvidasPeloBot.length

        const transferidas = listaFinalizados.filter(ehAtendimentoTransferido)
        this.transferenciasPorDia = transferidas.reduce((acc, r) => {
          const dataRef = r.fim_bot || r.fim || r.inicio
          if (!dataRef) return acc
          const key = dateToKey(dataRef)
          acc[key] = (acc[key] || 0) + 1
          return acc
        }, {})

        if (mensagensBotData != null) {
          if (typeof mensagensBotData.total === 'number') {
            this.totalMensagensBot = mensagensBotData.total
          } else if (Array.isArray(mensagensBotData.data)) {
            this.totalMensagensBot = mensagensBotData.data.reduce((s, item) => s + (Number(item.count) || Number(item.total) || 0), 0)
          } else if (typeof mensagensBotData === 'number') {
            this.totalMensagensBot = mensagensBotData
          } else {
            this.totalMensagensBot = 0
          }
        } else {
          this.totalMensagensBot = 0
        }

        const comCsat = resolvidasPeloBot.filter(r => r.csat_score >= 1 && r.csat_score <= 5)
        this.totalRespostasCsat = comCsat.length
        const somaCsat = comCsat.reduce((s, r) => s + Number(r.csat_score), 0)
        this.mediaCsat = this.totalRespostasCsat > 0 ? somaCsat / this.totalRespostasCsat : 0

        let somaTempoSegBot = 0
        let countTempoBot = 0
        lista.forEach(r => {
          const seg = calcularTempoBotSegundos(r)
          if (seg > 0) {
            somaTempoSegBot += seg
            countTempoBot += 1
          }
        })
        this.tempoResolucaoMediaMin = countTempoBot > 0 ? (somaTempoSegBot / countTempoBot) / 60 : 0

        this.resolucaoPorDia = resolvidasPeloBot.reduce((acc, r) => {
          const dataRef = r.fim || r.ultima_mensagem_em || r.data_resolucao || r.inicio
          if (!dataRef) return acc
          const key = dateToKey(dataRef)
          acc[key] = (acc[key] || 0) + 1
          return acc
        }, {})
      } catch (err) {
        console.error('Erro ao carregar relatório IA/BOT:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar dados.', { title: 'Erro', variant: 'danger', solid: true })
        this.numeroConversas = 0
        this.conversasResolvidasPeloBot = 0
        this.totalMensagensBot = 0
        this.tempoResolucaoMediaMin = 0
        this.totalRespostasCsat = 0
        this.mediaCsat = 0
        this.resolucaoPorDia = {}
        this.transferenciasPorDia = {}
      } finally {
        this.carregando = false
      }
    },
  },
}
</script>

<style>
body:has(.relatorio-ia-bot-page) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100% !important;
}
body:has(.relatorio-ia-bot-page) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}
body:has(.relatorio-ia-bot-page) .content-wrapper,
body:has(.relatorio-ia-bot-page) .content-body {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}
body:has(.relatorio-ia-bot-page) .content-right {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@600;700;800&display=swap');

.relatorio-ia-bot-page {
  --mc-bg: #f6f2fa;
  --mc-surface: #ffffff;
  --mc-surface-soft: #fbf8fe;
  --mc-surface-tint: #f8f3fc;
  --mc-border: #eadff6;

  --mc-text: #4b2a7b;
  --mc-text-soft: #7f6b9e;
  --mc-text-muted: #a190bc;

  --mc-purple: #7b3fe4;
  --mc-purple-2: #a248d9;
  --mc-pink: #e46aa9;
  --mc-coral: #ff8a7a;

  --mc-success: #59c9a5;
  --mc-warning: #f6c768;
  --mc-danger: #f27d7d;
  --mc-blue: #7aa7ff;

  --mc-gradient-main: linear-gradient(135deg, #6f35d0 0%, #b43fcf 48%, #ff7b6e 100%);
  --mc-gradient-card: linear-gradient(135deg, #9d4edd 0%, #e46aa9 55%, #ffb3a7 100%);

  --mc-shadow-lg: 0 20px 50px rgba(116, 57, 176, 0.18);
  --mc-shadow-md: 0 10px 30px rgba(93, 42, 140, 0.08);
  --mc-shadow-sm: 0 6px 18px rgba(93, 42, 140, 0.05);

  --mc-radius-xl: 30px;
  --mc-radius-lg: 24px;
  --mc-radius-md: 20px;
  --mc-radius-sm: 14px;
  --mc-radius-pill: 999px;

  background: var(--mc-bg);
  padding: clamp(1rem, 2vw, 1.75rem) clamp(1rem, 2.5vw, 2rem) 2.5rem;
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
  overflow-x: hidden;
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--mc-text);
}

.relatorio-ia-bot-page .mc-main-row {
  margin-left: -0.6rem;
  margin-right: -0.6rem;
}

.relatorio-ia-bot-page .mc-col-left,
.relatorio-ia-bot-page .mc-col-right {
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}

.mc-sec-head {
  margin-bottom: 1.5rem;
}

.mc-sec-head__title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: clamp(1.35rem, 2.8vw, 1.75rem);
  font-weight: 800;
  color: var(--mc-text);
  margin: 0 0 0.35rem;
  letter-spacing: -0.02em;
}

.mc-sec-head__sub {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--mc-text-muted);
}

.mc-sec-head__escopo {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-top: 1.15rem;
  padding: 1rem 1.2rem;
  border-radius: var(--mc-radius-md);
  background: linear-gradient(135deg, rgba(123, 63, 228, 0.12) 0%, rgba(228, 106, 169, 0.1) 50%, rgba(255, 138, 122, 0.08) 100%);
  border: 1px solid rgba(123, 63, 228, 0.22);
  box-shadow: var(--mc-shadow-sm);
}

.mc-sec-head__escopo-ico {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  color: var(--mc-purple) !important;
}

.mc-sec-head__escopo-texto {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--mc-text);
}

.mc-sec-head__escopo-texto strong {
  font-weight: 800;
  color: var(--mc-purple);
}

.mc-impacto-story {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  font-weight: 700;
  line-height: 1.45;
  color: var(--mc-text);
  margin: 0 0 1.25rem;
  padding: 1rem 1.15rem;
  border-radius: var(--mc-radius-md);
  background: var(--mc-surface-tint);
  border: 1px solid var(--mc-border);
  box-shadow: var(--mc-shadow-sm);
}

.mc-aux {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.15rem;
  border-radius: var(--mc-radius-md);
  background: var(--mc-surface);
  border: 1px solid var(--mc-border);
  box-shadow: var(--mc-shadow-sm);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.mc-aux:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(93, 42, 140, 0.1);
}

.mc-aux__ico {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(123, 63, 228, 0.14) 0%, rgba(255, 138, 122, 0.12) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mc-purple) !important;
  flex-shrink: 0;
}

.mc-aux__k {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--mc-text);
  line-height: 1.1;
}

.mc-aux__t {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--mc-text-soft);
  margin-top: 0.15rem;
}

.mc-mock-banner {
  margin-top: 1.5rem;
  padding: 0.85rem 1rem;
  border-radius: var(--mc-radius-md);
  background: rgba(123, 63, 228, 0.08);
  border: 1px dashed var(--mc-border);
  font-size: 0.85rem;
  color: var(--mc-text-soft);
}

.mc-mock-banner code {
  font-size: 0.8em;
  color: var(--mc-purple);
  background: rgba(255, 255, 255, 0.8);
  padding: 0.1rem 0.35rem;
  border-radius: 6px;
}
</style>
